/**
 * The Bridge mark draws itself in (only the drawing moves; the mark itself stays still) when its link or button is pointed at (or reached with the keyboard; a click does not count). Once it
 * has started it always plays to the end — sweeping the pointer back and forth over it does not restart it — and
 * only pointing at it again after it has finished starts it anew. The look lives in global.css
 * (`[data-bridge-mark][data-drawing]`); this only sets and clears `data-drawing`.
 */
const MARK = 'svg[data-bridge-mark]';

function markOf(target: EventTarget | null): { host: Element; mark: SVGElement } | null {
  if (!(target instanceof Element)) return null;
  const host = target.closest('a, button');
  if (!host || host.matches(':disabled')) return null;
  const mark = host.querySelector<SVGElement>(MARK);
  return mark ? { host, mark } : null;
}

function start(mark: SVGElement): void {
  if (mark.hasAttribute('data-drawing')) return;
  mark.setAttribute('data-drawing', '');
}

/** Listens on the whole document, once; returns the call that stops listening. */
export function installMarkDraw(doc: Document = document): () => void {
  const onOver = (event: MouseEvent): void => {
    const found = markOf(event.target);
    if (!found) return;
    // only entering the link or button counts, not moving between its own parts
    if (event.relatedTarget instanceof Node && found.host.contains(event.relatedTarget)) return;
    start(found.mark);
  };
  const onFocus = (event: FocusEvent): void => {
    const found = markOf(event.target);
    // only focus from the keyboard: a click also focuses the link, and must not draw the mark a second time
    if (found?.host === event.target && found.host.matches(':focus-visible')) start(found.mark);
  };
  const onEnd = (event: AnimationEvent): void => {
    // the last link of the last drawing layer finishing is the end: the mark is ready to draw again
    if (event.animationName !== 'markDraw' || !(event.target instanceof Element)) return;
    const mark = event.target.closest(MARK);
    const layers = mark?.querySelectorAll('[data-mark-top]');
    const last = layers?.[layers.length - 1]?.lastElementChild;
    if (mark && last === event.target) mark.removeAttribute('data-drawing');
  };
  doc.addEventListener('mouseover', onOver);
  doc.addEventListener('focusin', onFocus);
  doc.addEventListener('animationend', onEnd);
  return () => {
    doc.removeEventListener('mouseover', onOver);
    doc.removeEventListener('focusin', onFocus);
    doc.removeEventListener('animationend', onEnd);
  };
}
